import moment from 'moment';

export interface DataFilterOptions {
  dateRange?: {
    startDate: Date | null;
    endDate: Date | null;
  };
  assignedTo?: string | null;
  flagged?: boolean | null;
  enrichmentStatus?: string | null;
  tags?: string[];
}

export interface DataItem {
  _id?: string;
  createdAt?: string | Date;
  updatedAt?: string | Date;
  assignedTo?: string;
  flagged?: boolean;
  enrichmentStatus?: string;
  dataTags?: string[];
  data?: any;
  [key: string]: any;
}

/**
 * Filter data items based on provided filter options
 */
export const filterDataItems = (
  data: DataItem[],
  filters: DataFilterOptions,
  searchQuery?: string
): DataItem[] => {
  if (!Array.isArray(data) || data.length === 0) {
    return [];
  }

  let filtered = [...data];

  // Apply date range filter
  if (filters.dateRange?.startDate || filters.dateRange?.endDate) {
    filtered = filtered.filter((item) => {
      const itemDate = item.createdAt || item.updatedAt;
      if (!itemDate) return false;

      const date = moment(itemDate);
      const startDate = filters.dateRange?.startDate
        ? moment(filters.dateRange.startDate).startOf('day')
        : null;
      const endDate = filters.dateRange?.endDate
        ? moment(filters.dateRange.endDate).endOf('day')
        : null;

      if (startDate && date.isBefore(startDate)) return false;
      if (endDate && date.isAfter(endDate)) return false;

      return true;
    });
  }

  // Apply assignedTo filter
  if (filters.assignedTo !== undefined && filters.assignedTo !== null) {
    if (filters.assignedTo === '') {
      // Filter for unassigned items
      filtered = filtered.filter(
        (item) => !item.assignedTo && !item.data?.assignedTo
      );
    } else {
      // Filter for specific user
      filtered = filtered.filter((item) => {
        const assignedId = item.assignedTo || item.data?.assignedTo;
        return assignedId?.toString() === filters.assignedTo?.toString();
      });
    }
  }

  // Apply flagged filter
  if (filters.flagged !== undefined && filters.flagged !== null) {
    filtered = filtered.filter((item) => item.flagged === filters.flagged);
  }

  // Apply enrichment status filter
  if (filters.enrichmentStatus) {
    filtered = filtered.filter(
      (item) => item.enrichmentStatus === filters.enrichmentStatus
    );
  }

  // Apply tags filter
  if (filters.tags && filters.tags.length > 0) {
    filtered = filtered.filter((item) => {
      const itemTags = item.dataTags || [];
      return filters.tags!.some((tag) =>
        itemTags.some(
          (itemTag) =>
            itemTag.toLowerCase() === tag.toLowerCase() ||
            itemTag.toLowerCase().includes(tag.toLowerCase())
        )
      );
    });
  }

  // Apply search query if provided
  if (searchQuery && searchQuery.trim()) {
    const formattedQuery = searchQuery.toLowerCase().replace(/ /g, '_');
    filtered = filterBySearchQuery(filtered, formattedQuery);
  }

  return filtered;
};

/**
 * Filter items by search query (searches through nested objects)
 */
const filterBySearchQuery = (data: DataItem[], query: string): DataItem[] => {
  const searchNestedObject = (obj: any): boolean => {
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];

        if (value !== null && typeof value === 'object') {
          // Recursively search nested objects
          if (searchNestedObject(value)) {
            return true;
          }
        } else {
          // Check if the string representation of the value includes the query
          if (
            value &&
            value.toString().toLowerCase().includes(query)
          ) {
            return true;
          }
        }
      }
    }
    return false;
  };

  return data.filter((item) => searchNestedObject(item));
};

/**
 * Check if any filters are active
 */
export const hasActiveFilters = (filters: DataFilterOptions): boolean => {
  return (
    (filters.dateRange?.startDate !== null &&
      filters.dateRange?.startDate !== undefined) ||
    (filters.dateRange?.endDate !== null &&
      filters.dateRange?.endDate !== undefined) ||
    (filters.assignedTo !== undefined && filters.assignedTo !== null) ||
    (filters.flagged !== undefined && filters.flagged !== null) ||
    (filters.enrichmentStatus !== undefined &&
      filters.enrichmentStatus !== null) ||
    (filters.tags !== undefined && filters.tags.length > 0)
  );
};

/**
 * Reset all filters to default state
 */
export const resetFilters = (): DataFilterOptions => {
  return {
    dateRange: {
      startDate: null,
      endDate: null,
    },
    assignedTo: null,
    flagged: null,
    enrichmentStatus: null,
    tags: [],
  };
};






